import { type ComponentType, type ReactNode } from 'react';

import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import { LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

export interface WizardHatProps extends Omit<StackProps, 'title'> {
	description?: ReactNode;
	Icon?: ComponentType<LucideSvgProps> | null;
	iconProps?: LucideSvgProps;
	progressBarSlot?: ReactNode;
	stepSubtitle?: ReactNode;
	stepTitle?: ReactNode;
	title: ReactNode;
}

const WizardHat = ({
	description,
	Icon,
	iconProps,
	progressBarSlot,
	stepSubtitle,
	stepTitle,
	title,
	...rest
}: WizardHatProps) => {
	const { sx: iconSx, ...restIconProps } = iconProps || {};

	return (
		<Stack {...rest}>
			{(stepTitle || stepSubtitle || Icon || progressBarSlot) && (
				<Stack
					sx={{
						flexDirection: 'row',
						alignItems: 'center',
						justifyContent: 'space-between',
						flexWrap: 'wrap',
						gap: 150,
						marginBottom: 150,
					}}
				>
					<Stack
						sx={{
							flexDirection: 'row',
							alignItems: 'center',
							flexWrap: 'wrap',
						}}
					>
						{Icon && (
							<Icon
								sx={combineSx(
									({ size, vars }) => ({
										height: size.xS,
										width: size.xS,
										color: vars.palette.uc.highlight.icon.main,
										marginRight: 100,
										flexShrink: 0,
									}),
									iconSx,
								)}
								{...restIconProps}
							/>
						)}

						{stepTitle && (
							<Typography
								variant="body-sm"
								sx={{ fontWeight: 500, marginRight: 100 }}
							>
								{stepTitle}
							</Typography>
						)}

						{stepSubtitle && (
							<Typography
								variant="body-xs"
								sx={{
									color: ({ vars }) => vars.palette.uc.text.weak,
									textWrap: 'nowrap',
								}}
							>
								{stepSubtitle}
							</Typography>
						)}
					</Stack>

					{progressBarSlot}
				</Stack>
			)}

			<Typography
				variant="title-lg"
				sx={{ fontWeight: 500, marginBottom: 150 }}
			>
				{title}
			</Typography>

			{description && <Typography>{description}</Typography>}
		</Stack>
	);
};

export default WizardHat;
